<script lang="ts" setup>
import { defineProps, defineEmits, ref } from "vue";
import InputModal from "../InputModal.vue";
import SlideImagesBlock from "./SlideImagesBlock.vue";
import CheckListBlock from "./CheckListBlock.vue";
import AddImageModal from "../AddImageModal.vue";
import CustomAccordion from "./CustomAccordion.vue";

type Props = {
  input: any;
  project: string | string[];
  phase: number;
  phaseDetails: any;
  step: any;
  showDetails?: boolean;
  isAccordion?: boolean;
  phaseStatus?: string;
};
type Emits = {
  (event: "refresh"): void;
};

const emits = defineEmits<Emits>();

const props = defineProps<Props>();

const isInputModalOpen = ref(false);
const isAddImageModalOpen = ref(false);
const selectedInput = ref<any>(null);
const selectedStep = ref<any>(null);
const selectedInputGroupIndex = ref<any>(null);
const selectedInputIndex = ref<any>(null);

const toggleModalHandler: any = (
  input: any,
  step: any,
  inputIndex?: number,
  groupIndex?: number
) => {
  selectedInput.value = input;
  selectedStep.value = step;
  isInputModalOpen.value = !isInputModalOpen.value;
  selectedInputGroupIndex.value = groupIndex;
  selectedInputIndex.value = inputIndex;
};
</script>
<template>
  <InputModal
    v-if="isInputModalOpen && selectedInput"
    :input="selectedInput"
    :step="selectedStep"
    :is-open="isInputModalOpen"
    :project="project"
    :phase="phase"
    :termins="phaseDetails.termins"
    :input-index="selectedInputIndex"
    :group-index="selectedInputGroupIndex"
    @refresh="() => emits('refresh')"
    @toggle-modal="
      () => {
        isInputModalOpen = false;
        selectedInput = null;
        selectedStep = null;
      }
    "
  />
  <AddImageModal
    v-if="isAddImageModalOpen && selectedInput"
    :steps="phaseDetails.steps"
    :termins="phaseDetails.termins"
    :is-open="isAddImageModalOpen"
    :project="project"
    :input="selectedInput"
    :step="selectedStep"
    :header-title="selectedInput ? selectedInput.name : ''"
    :phase="phase"
    :group-index="selectedInputGroupIndex"
    :input-index="selectedInputIndex"
    @toggle-modal="isAddImageModalOpen = false"
    @refresh="() => emits('refresh')"
  />
  <div>
    <div
      :class="`${
        input.isConditionallyRepetable ? 'bg-transparent' : 'bg-[#E5E4E2]'
      }  rounded-primary-radius`"
    >
      <CustomAccordion
        v-if="isAccordion && !input.isConditionallyRepetable"
        :step-name="`${!input.isConditionallyRepetable ? input.name : ''}`"
        :sticky-height="`${
          showDetails ? 'top-[383px]' : 'top-[180px]'
        } sticky z-[16]`"
      >
        <div class="flex flex-col gap-3 relative p-3">
          <div
            v-for="repeatableInput in input.value.filter((item:any)=> item.value)"
            :key="repeatableInput.index"
          >
            <!-- CHECKLIST -->
            <div
              v-if="input.subtype === 'checkbox' && repeatableInput.value"
              class="p-3 w-full bg-white rounded-primary-radius shadow-primary-box-shadow"
            >
              <CheckListBlock
                :checklist="[
                  {
                    id: repeatableInput.index,
                    name: `${input.repeatableInputNoun} ${
                      repeatableInput.index + 1
                    }`,
                    value: repeatableInput.value,
                  },
                ]"
              />
            </div>
            <!-- IMAGE -->
            <div
              v-if="
                input.subtype === 'image' &&
                repeatableInput.value &&
                repeatableInput.value.length > 0
              "
            >
              <div :key="repeatableInput.index" class="flex flex-col gap-1">
                <SlideImagesBlock
                  :is-group="true"
                  :show-details="showDetails"
                  :header-class="`${
                    showDetails ? 'top-[387px]' : 'top-[187px]'
                  } sticky z-[15]`"
                  @add-image-modal-open="
                    () => {
                      selectedInput = input;
                      selectedStep = step;
                      isAddImageModalOpen = true;
                      selectedInputGroupIndex = input.repeatableGroupIndex;
                      selectedInputIndex = repeatableInput.index;
                    }
                  "
                  :input="{
                    id: input.id,
                    name: `${input.repeatableInputNoun} ${
                      repeatableInput.index + 1
                    }`,
                    value: repeatableInput.value,
                  }"
                  :step="step"
                  :steps="phaseDetails.steps"
                  :refresh="() => $emit('refresh')"
                />
              </div>
            </div>

            <!-- TEXT -->
            <div v-if="input.subtype === 'text'" class="flex flex-col gap-3">
              <div
                class="p-3 w-full bg-white rounded-primary-radius shadow-primary-box-shadow flex flex-col gap-2"
              >
                <!-- Top side -->
                <div class="w-full flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="flex items-center gap-1">
                      <img
                        class="w-3 h-3 cursor-pointer"
                        src="@/assets/calendar-icon.svg"
                        alt="Calendar"
                      />

                      <p class="text-sm text-[#898790] foont-medium">
                        {{
                          repeatableInput.createdAt
                            ? new Date(
                                repeatableInput.createdAt
                              ).toLocaleDateString("de")
                            : ""
                        }}
                      </p>
                    </div>
                    <div class="flex items-center gap-1">
                      <img
                        class="w-3 h-3"
                        src="@/assets/gray-user.svg"
                        alt="User"
                      />
                      <p
                        v-for="(worker, index) in repeatableInput.workers"
                        :key="index"
                        class="text-sm text-[#898790] foont-medium"
                      >
                        {{ worker.fullname
                        }}<span
                          v-if="index < repeatableInput.workers.length - 1"
                          >,</span
                        >
                      </p>
                    </div>
                  </div>
                  <img
                    @click="
                      toggleModalHandler(
                        input,
                        step,
                        repeatableInput.index,
                        input.repeatableGroupIndex
                      )
                    "
                    class="w-4 h-4 cursor-pointer"
                    src="@/assets/pencil-gray.svg"
                    alt="Pencil"
                  />
                </div>
                <!-- Content -->
                <div class="mt-4">
                  <p
                    v-if="input.isConditionallyRepetable"
                    class="text-base font-normal"
                  >
                    {{ input.name }} {{ repeatableInput.index + 1 }}
                  </p>
                  <p v-else class="text-base font-normal">
                    {{ input.repeatableInputNoun }}
                    {{ repeatableInput.index + 1 }}
                  </p>
                  <div class="p-3 rounded-primary-radius bg-[#e5ecf6] mt-2">
                    <p class="text-base font-normal">
                      {{ repeatableInput.value }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- NUMBER -->
            <div v-if="input.subtype === 'number'" class="flex gap-1">
              <div
                class="p-3 w-1/3 bg-white rounded-primary-radius shadow-primary-box-shadow"
              >
                <!-- Top side -->
                <div class="w-full flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="flex items-center gap-1">
                      <img
                        class="w-3 h-3 cursor-pointer"
                        src="@/assets/calendar-icon.svg"
                        alt="Calendar"
                      />

                      <p class="text-sm text-[#898790] foont-medium">
                        {{
                          repeatableInput.createdAt
                            ? new Date(
                                repeatableInput.createdAt
                              ).toLocaleDateString("de")
                            : ""
                        }}
                      </p>
                    </div>
                    <div class="flex items-center gap-1">
                      <img
                        class="w-3 h-3"
                        src="@/assets/gray-user.svg"
                        alt="User"
                      />
                      <p
                        v-for="(worker, index) in repeatableInput.workers"
                        :key="index"
                        class="text-sm text-[#898790] font-medium"
                      >
                        {{ worker.fullname
                        }}<span
                          v-if="index < repeatableInput.workers.length - 1"
                          >,</span
                        >
                      </p>
                    </div>
                  </div>
                  <img
                    @click="
                      toggleModalHandler(
                        input,
                        step,
                        repeatableInput.index,
                        input.repeatableGroupIndex
                      )
                    "
                    class="w-4 h-4 cursor-pointer"
                    src="@/assets/pencil-gray.svg"
                    alt="Pencil"
                  />
                </div>
                <!-- Content -->
                <div class="mt-4 flex gap-2">
                  <p class="text-base font-normal grow">
                    {{ input.repeatableInputNoun }}
                    {{ repeatableInput.index + 1 }}
                  </p>
                  <div
                    class="px-3 py-[1px] rounded-primary-radius bg-[#e5ecf6] mt-2 h-fit w-fit"
                  >
                    <p class="text-base font-normal h-fit w-fit">
                      {{ repeatableInput.value }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- SELECT -->
            <div
              v-if="
                input.subtype === 'select' || input.subtype === 'static_select'
              "
              class="flex flex-col gap-3"
            >
              <div
                class="p-3 w-full bg-white rounded-primary-radius shadow-primary-box-shadow flex flex-col gap-2"
              >
                <!-- Top side -->
                <div class="w-full flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="flex items-center gap-1">
                      <img
                        class="w-3 h-3 cursor-pointer"
                        src="@/assets/calendar-icon.svg"
                        alt="Calendar"
                      />

                      <p class="text-sm text-[#898790] foont-medium">
                        {{
                          repeatableInput.createdAt
                            ? new Date(
                                repeatableInput.createdAt
                              ).toLocaleDateString("de")
                            : ""
                        }}
                      </p>
                    </div>
                    <div class="flex items-center gap-1">
                      <img
                        class="w-3 h-3"
                        src="@/assets/gray-user.svg"
                        alt="User"
                      />
                      <p
                        v-for="(worker, index) in repeatableInput.workers"
                        :key="index"
                        class="text-sm text-[#898790] font-medium"
                      >
                        {{ worker.fullname
                        }}<span
                          v-if="index < repeatableInput.workers.length - 1"
                          >,</span
                        >
                      </p>
                    </div>
                  </div>
                  <img
                    @click="
                      toggleModalHandler(
                        input,
                        step,
                        repeatableInput.index,
                        input.repeatableGroupIndex
                      )
                    "
                    class="w-4 h-4 cursor-pointer"
                    src="@/assets/pencil-gray.svg"
                    alt="Pencil"
                  />
                </div>

                <!-- Content -->
                <div class="mt-4">
                  <p class="text-base font-normal text-">
                    {{ input.repeatableInputNoun }}
                    {{ repeatableInput.index + 1 }}
                  </p>
                  <div class="p-3 rounded-primary-radius bg-[#e5ecf6] mt-2">
                    <p class="text-base font-normal text-">
                      {{
                        repeatableInput.value
                          ? input.options.find(
                              (option: any) =>
                                Number(option.id) ===
                                Number(repeatableInput.value)
                            )?.option
                          : ""
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CustomAccordion>
      <div v-else>
        <div class="flex flex-col gap-3 relative">
          <h2
            v-if="!input.isConditionallyRepetable"
            class="text-lg leading-[32px] tracking-[0.01rem] text-primary-text-color font-semibold"
          >
            {{ input.name }}
          </h2>
          <div
            v-for="repeatableInput in input.value.filter((item:any)=> item.value  && item.value.length > 0)"
            :key="repeatableInput.index"
            class="bg-[#E5E4E2] rounded-primary-radius p-3"
          >
            <!-- CHECKLIST -->
            <div
              v-if="input.subtype === 'checkbox' && repeatableInput.value"
              class="p-3 w-full bg-white rounded-primary-radius shadow-primary-box-shadow"
            >
              <CheckListBlock
                :checklist="[
                  {
                    id: repeatableInput.index,
                    name: `${input.repeatableInputNoun} ${
                      repeatableInput.index + 1
                    }`,
                    value: repeatableInput.value,
                  },
                ]"
              />
            </div>
            <!-- IMAGE -->
            <div v-if="input.subtype === 'image'">
              <div :key="repeatableInput.index" class="flex flex-col gap-1">
                <SlideImagesBlock
                  :is-group="true"
                  :show-details="showDetails"
                  :header-class="`${
                    showDetails ? 'top-[387px]' : 'top-[187px]'
                  } sticky z-[11]`"
                  @add-image-modal-open="
                    () => {
                      selectedInput = input;
                      selectedStep = step;
                      isAddImageModalOpen = true;
                      selectedInputGroupIndex = input.repeatableGroupIndex;
                      selectedInputIndex = repeatableInput.index;
                    }
                  "
                  :input="{
                    id: input.id,
                    name: `${
                      input.isConditionallyRepetable
                        ? input.name
                        : input.repeatableInputNoun
                    } ${repeatableInput.index + 1}`,
                    value: repeatableInput.value,
                  }"
                  :step="step"
                  :steps="phaseDetails.steps"
                  :refresh="() => $emit('refresh')"
                />
              </div>
            </div>

            <!-- TEXT -->
            <div v-if="input.subtype === 'text'" class="flex flex-col gap-3">
              <div
                class="p-3 w-full bg-white rounded-primary-radius shadow-primary-box-shadow flex flex-col gap-2"
              >
                <!-- Top side -->
                <div class="w-full flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="flex items-center gap-1">
                      <img
                        class="w-3 h-3 cursor-pointer"
                        src="@/assets/calendar-icon.svg"
                        alt="Calendar"
                      />

                      <p class="text-sm text-[#898790] foont-medium">
                        {{
                          repeatableInput.createdAt
                            ? new Date(
                                repeatableInput.createdAt
                              ).toLocaleDateString("de")
                            : ""
                        }}
                      </p>
                    </div>
                    <div class="flex items-center gap-1">
                      <img
                        class="w-3 h-3"
                        src="@/assets/gray-user.svg"
                        alt="User"
                      />
                      <p
                        v-for="(worker, index) in repeatableInput.workers"
                        :key="index"
                        class="text-sm text-[#898790] foont-medium"
                      >
                        {{ worker?.fullname
                        }}<span
                          v-if="index < repeatableInput.workers.length - 1"
                          >,</span
                        >
                      </p>
                    </div>
                  </div>
                  <img
                    @click="
                      toggleModalHandler(
                        input,
                        step,
                        repeatableInput.index,
                        input.repeatableGroupIndex
                      )
                    "
                    class="w-4 h-4 cursor-pointer"
                    src="@/assets/pencil-gray.svg"
                    alt="Pencil"
                  />
                </div>
                <!-- Content -->
                <div class="mt-4">
                  <p
                    v-if="input.isConditionallyRepetable"
                    class="text-base font-normal"
                  >
                    {{ input.name }} {{ repeatableInput.index + 1 }}
                  </p>
                  <p v-else class="text-base font-normal text-">
                    {{ input.name }}
                  </p>
                  <div class="p-3 rounded-primary-radius bg-[#e5ecf6] mt-2">
                    <p class="text-base font-normal text-">
                      {{ repeatableInput.value }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- NUMBER -->
            <div v-if="input.subtype === 'number'" class="flex gap-1">
              <div
                class="p-3 w-1/3 bg-white rounded-primary-radius shadow-primary-box-shadow"
              >
                <!-- Top side -->
                <div class="w-full flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="flex items-center gap-1">
                      <img
                        class="w-3 h-3 cursor-pointer"
                        src="@/assets/calendar-icon.svg"
                        alt="Calendar"
                      />

                      <p class="text-sm text-[#898790] foont-medium">
                        {{
                          repeatableInput.createdAt
                            ? new Date(
                                repeatableInput.createdAt
                              ).toLocaleDateString("de")
                            : ""
                        }}
                      </p>
                    </div>
                    <div class="flex items-center gap-1">
                      <img
                        class="w-3 h-3"
                        src="@/assets/gray-user.svg"
                        alt="User"
                      />
                      <p
                        v-for="(worker, index) in repeatableInput.workers"
                        :key="index"
                        class="text-sm text-[#898790] font-medium"
                      >
                        {{ worker.fullname
                        }}<span
                          v-if="index < repeatableInput.workers.length - 1"
                          >,</span
                        >
                      </p>
                    </div>
                  </div>
                  <img
                    @click="
                      toggleModalHandler(
                        input,
                        step,
                        repeatableInput.index,
                        input.repeatableGroupIndex
                      )
                    "
                    class="w-4 h-4 cursor-pointer"
                    src="@/assets/pencil-gray.svg"
                    alt="Pencil"
                  />
                </div>
                <!-- Content -->
                <div class="mt-4 flex gap-2">
                  <p
                    v-if="input.isConditionallyRepetable"
                    class="text-base font-normal"
                  >
                    {{ input.name }} {{ repeatableInput.index + 1 }}
                  </p>
                  <p v-else class="text-base font-normal grow">
                    {{ input.name }}
                  </p>
                  <div
                    class="px-3 py-[1px] rounded-primary-radius bg-[#e5ecf6] mt-2 h-fit w-fit"
                  >
                    <p class="text-base font-normal h-fit w-fit">
                      {{ repeatableInput.value }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- SELECT -->
            <div
              v-if="
                input.subtype === 'select' || input.subtype === 'static_select'
              "
              class="flex flex-col gap-3"
            >
              <div
                class="p-3 w-full bg-white rounded-primary-radius shadow-primary-box-shadow flex flex-col gap-2"
              >
                <!-- Top side -->
                <div class="w-full flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="flex items-center gap-1">
                      <img
                        class="w-3 h-3 cursor-pointer"
                        src="@/assets/calendar-icon.svg"
                        alt="Calendar"
                      />

                      <p class="text-sm text-[#898790] foont-medium">
                        {{
                          repeatableInput.createdAt
                            ? new Date(
                                repeatableInput.createdAt
                              ).toLocaleDateString("de")
                            : ""
                        }}
                      </p>
                    </div>
                    <div class="flex items-center gap-1">
                      <img
                        class="w-3 h-3"
                        src="@/assets/gray-user.svg"
                        alt="User"
                      />
                      <p
                        v-for="(worker, index) in repeatableInput.workers"
                        :key="index"
                        class="text-sm text-[#898790] font-medium"
                      >
                        {{ worker.fullname
                        }}<span
                          v-if="index < repeatableInput.workers.length - 1"
                          >,</span
                        >
                      </p>
                    </div>
                  </div>
                  <img
                    @click="
                      toggleModalHandler(
                        input,
                        step,
                        repeatableInput.index,
                        input.repeatableGroupIndex
                      )
                    "
                    class="w-4 h-4 cursor-pointer"
                    src="@/assets/pencil-gray.svg"
                    alt="Pencil"
                  />
                </div>
                <!-- Content -->
                <div class="mt-4">
                  <p
                    v-if="input.isConditionallyRepetable"
                    class="text-base font-normal"
                  >
                    {{ input.name }} {{ repeatableInput.index + 1 }}
                  </p>
                  <p v-else class="text-base font-normal text-">
                    {{ input.name }}
                  </p>
                  <div class="p-3 rounded-primary-radius bg-[#e5ecf6] mt-2">
                    <p class="text-base font-normal text-">
                      {{ repeatableInput.value }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
